// Groupes Publics - Version avec création fonctionnelle
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Hash, Users, Lock, Globe, Plus, Search, MessageCircle, Bell, BellOff, Settings, MoreHorizontal, Crown, ChevronRight, Mic, Video, MessageSquarePlus, X, Loader2, Trash2, Image, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Données démo
const DEMO_MES_SALONS = [];

const DEMO_SALONS_INSCRITS = [];

const SALONS_SUGGERES = [
  {
    id: '7',
    nom: 'Plomberie Sans Stress',
    description: 'Conseils et astuces plomberie pour les particuliers.',
    membres: 345,
    image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=100',
    prive: false
  },
  {
    id: '8',
    nom: 'Menuiserie Créative',
    description: 'Projets bois, ébénisterie et travail du bois.',
    membres: 567,
    image: 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=100',
    prive: false
  }
];

// Modal de création de salon
function CreateSalonModal({ isOpen, onClose, onCreate }) {
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [prive, setPrive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isOpen || !isMobile) { setKeyboardHeight(0); return; }
    const viewport = window.visualViewport;
    if (!viewport) return;
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    return () => {
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  const handleSubmit = async () => {
    if (!nom.trim()) { toast.error('Donne un nom à ton groupe public'); return; }
    setCreating(true);
    await onCreate({ nom, description, image: imageUrl || null, prive });
    setCreating(false);
    setNom(''); setDescription(''); setImageUrl(''); setPrive(false);
    onClose();
  };

  if (!isOpen) return null;

  const formFields = (
    <>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Nom du groupe *</label>
        <input type="text" value={nom} onChange={e => setNom(e.target.value)}
          placeholder="Ex: Bricoleurs du dimanche"
          className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
          data-testid="salon-name" />
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
          placeholder="Décris le thème et l'objectif de ton groupe..."
          className="w-full p-4 rounded-xl border text-[15px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 resize-none"
          data-testid="salon-description" />
      </div>
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Image (URL)</label>
        <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)}
          placeholder="https://..."
          className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400" />
      </div>
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-3">
          {prive ? <Lock size={20} className="text-gray-600" /> : <Globe size={20} className="text-gray-600" />}
          <div>
            <p className="font-medium text-gray-900 text-[15px]">{prive ? 'Groupe privé' : 'Groupe public'}</p>
            <p className="text-sm text-gray-500">{prive ? 'Invitation requise pour rejoindre' : 'Tout le monde peut rejoindre'}</p>
          </div>
        </div>
        <button onClick={() => setPrive(!prive)}
          className={`w-12 h-7 rounded-full transition-colors flex-shrink-0 ${prive ? 'bg-[#1E3A5F]' : 'bg-gray-300'}`}>
          <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${prive ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
    </>
  );

  if (isMobile) {
    return createPortal(
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        <header className="flex items-center gap-2 px-3 border-b border-gray-100"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}>
          <button onClick={onClose} className="p-1 text-gray-500"><ArrowLeft size={22} /></button>
          <h1 className="text-[15px] font-bold text-gray-900">Créer un groupe public</h1>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{formFields}</div>
        <div className="p-4 border-t border-gray-100 bg-white"
          style={{
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}>
          <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200">
            <Sparkles size={16} />Créer avec Abracada
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]">Annuler</button>
            <button onClick={handleSubmit} disabled={creating}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 flex items-center justify-center disabled:opacity-50">
              {creating ? <Loader2 size={18} className="animate-spin" /> : 'Créer'}
            </button>
          </div>
        </div>
      </motion.div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[10002] bg-black/60 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Créer un groupe public</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">{formFields}</div>
        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]">Annuler</button>
          <button onClick={handleSubmit} disabled={creating}
            className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 flex items-center justify-center disabled:opacity-50">
            {creating ? <Loader2 size={18} className="animate-spin" /> : 'Créer le groupe'}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

function SalonCard({ salon, isMine = false, isDemo, onDelete }) {
  const [notifications, setNotifications] = useState(salon.notifications ?? true);

  const handleAction = () => {
    if (isDemo) {
      toast.error('Crée ton compte pour rejoindre ce groupe public !');
      return;
    }
    toast.info('Ouverture du salon...');
  };

  return (
    <div className={`bg-white rounded-xl border p-4 hover:shadow-md transition-all ${
      salon.actif === false ? 'opacity-60' : ''
    } ${salon.messages_non_lus > 0 ? 'border-[#1E3A5F]/30 bg-[#F0F4F8]/30' : 'border-gray-200'}`}>
      <div className="flex items-start gap-4">
        <div className="relative">
          <img 
            src={salon.image} 
            alt={salon.nom}
            className="w-14 h-14 rounded-xl object-cover"
          />
          {salon.prive && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gray-800 rounded-full flex items-center justify-center">
              <Lock size={10} className="text-white" />
            </div>
          )}
          {salon.role === 'admin' && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
              <Crown size={10} className="text-white" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{salon.nom}</h3>
            {salon.messages_non_lus > 0 && (
              <span className="flex-shrink-0 px-2 py-0.5 bg-[#1E3A5F] text-white text-xs font-bold rounded-full">
                {salon.messages_non_lus}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">{salon.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Users size={12} />
              {salon.membres} membres
            </span>
            {salon.derniere_activite && (
              <span>{salon.derniere_activite}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {isMine ? (
            <>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <Settings size={18} className="text-gray-400" />
              </button>
              {!isDemo && (
                <button 
                  onClick={() => onDelete && onDelete(salon.id)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={18} className="text-red-400" />
                </button>
              )}
            </>
          ) : (
            <button 
              onClick={() => setNotifications(!notifications)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {notifications ? (
                <Bell size={18} className="text-[#1E3A5F]" />
              ) : (
                <BellOff size={18} className="text-gray-400" />
              )}
            </button>
          )}
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <MoreHorizontal size={18} className="text-gray-400" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
        <button 
          onClick={handleAction}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <MessageCircle size={16} />
          Ouvrir
        </button>
        {salon.actif !== false && (
          <>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Appel vocal">
              <Mic size={18} className="text-gray-500" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Appel vidéo">
              <Video size={18} className="text-gray-500" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SalonsPage() {
  const { user } = useCurrentUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('tous');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [mesSalons, setMesSalons] = useState([]);
  const [salonsInscrits, setSalonsInscrits] = useState([]);
  const [loading, setLoading] = useState(true);

  const isDemo = user?.isDemo;

  // Charger les salons depuis l'API
  const fetchSalons = async () => {
    if (isDemo) {
      setMesSalons(DEMO_MES_SALONS);
      setSalonsInscrits(DEMO_SALONS_INSCRITS);
      setLoading(false);
      return;
    }
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/salons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMesSalons(data.created || []);
        setSalonsInscrits(data.joined || []);
      }
    } catch (err) {
      console.error('Erreur chargement salons:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    // Charger toujours en mode demo si pas de user
    fetchSalons();
  }, [user, isDemo]);

  const totalNonLus = [...mesSalons, ...salonsInscrits].reduce((acc, s) => acc + (s.messages_non_lus || 0), 0);

  const handleCreateSalon = () => {
    if (isDemo) {
      toast.error('Crée ton compte pour créer un groupe public public !');
      return;
    }
    setShowCreateModal(true);
  };

  const handleCreate = async (salonData) => {
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/salons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(salonData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setMesSalons(prev => [{ ...data.salon, role: 'admin' }, ...prev]);
        toast.success('Groupe public créé !');
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur création salon:', err);
      toast.error('Erreur réseau');
    }
  };

  const handleDelete = async (salonId) => {
    if (isDemo) return;
    if (!confirm('Supprimer ce groupe public ?')) return;
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/salons/${salonId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setMesSalons(prev => prev.filter(s => s.id !== salonId));
        toast.success('Salon supprimé');
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateSalonModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreate}
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Groupes Publics</h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalNonLus > 0 ? `${totalNonLus} messages non lus` : 'Rejoins des communautés thématiques'}
          </p>
        </div>
        <button 
          onClick={handleCreateSalon}
          className="flex items-center gap-2 px-4 py-2 bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white font-medium rounded-lg transition-colors"
          data-testid="create-salon-btn"
        >
          <Plus size={18} />
          Créer un groupe public
        </button>
      </div>

      {/* Banner démo */}
      {isDemo && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <Hash size={20} className="text-amber-500" />
          <div>
            <p className="font-medium text-amber-800">Mode Démonstration</p>
            <p className="text-sm text-amber-700">Ces salons sont des exemples. Crée ton compte pour rejoindre ou créer des groupes.</p>
          </div>
        </div>
      )}

      {/* Recherche */}
      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-xl">
        <Search size={14} className="text-gray-400 flex-shrink-0" />
        <input
          type="text"
          placeholder="Rechercher un groupe public..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-900 placeholder-gray-400"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { id: 'tous', label: 'Tous', count: mesSalons.length + salonsInscrits.length },
          { id: 'mes-salons', label: 'Groupes publics', count: mesSalons.length },
          { id: 'inscrits', label: 'Inscriptions', count: salonsInscrits.length },
          { id: 'decouvrir', label: 'Découvrir', count: null }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id 
                ? 'border-[#1E3A5F] text-[#1E3A5F]' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count !== null && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* État vide pour vrais utilisateurs */}
      {!isDemo && mesSalons.length === 0 && salonsInscrits.length === 0 && activeTab !== 'decouvrir' && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <MessageSquarePlus size={32} className="text-purple-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Crée ton premier groupe public</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Rejoins des groupes publics thématiques pour échanger avec la communauté ou crée le tien !
          </p>
          <div className="flex justify-center gap-3">
            <button 
              onClick={() => setActiveTab('decouvrir')}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Découvrir des groupes publics
            </button>
            <button 
              onClick={handleCreateSalon}
              className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              Créer un groupe public
            </button>
          </div>
        </div>
      )}

      {/* Contenu */}
      {(activeTab === 'tous' || activeTab === 'mes-salons') && mesSalons.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Crown size={14} className="text-yellow-500" />
            Groupes publics créés
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {mesSalons.filter(s => 
              s.nom.toLowerCase().includes(searchQuery.toLowerCase())
            ).map(salon => (
              <SalonCard 
                key={salon.id} 
                salon={salon} 
                isMine={true} 
                isDemo={isDemo}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      {(activeTab === 'tous' || activeTab === 'inscrits') && salonsInscrits.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Hash size={14} />
            Salons rejoints
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {salonsInscrits.filter(s => 
              s.nom.toLowerCase().includes(searchQuery.toLowerCase())
            ).map(salon => (
              <SalonCard key={salon.id} salon={salon} isDemo={isDemo} />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'decouvrir' && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Globe size={14} />
            Salons suggérés pour toi
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {SALONS_SUGGERES.map(salon => (
              <div key={salon.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <img 
                    src={salon.image} 
                    alt={salon.nom}
                    className="w-14 h-14 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{salon.nom}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">{salon.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Users size={12} />
                        {salon.membres} membres
                      </span>
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white rounded-lg text-sm font-medium transition-colors">
                    Rejoindre
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <button className="text-[#1E3A5F] hover:underline font-medium text-sm flex items-center gap-1 mx-auto">
              Explorer plus de salons
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
